import { Button } from '@clientbase/clientbase-library'
import { Box, Skeleton, Stack, Typography } from '@mui/material'

import { theme } from 'styles/theme'

type NewPlanCardProps = {
  title: string
  subtitle: string
  intro: string
  features: string[]
  price: number
  isActive: boolean
  isRecommended?: boolean
  loading?: boolean
  onSubscribe: () => void
}

const NewPlanCard = ({
  title,
  subtitle,
  intro,
  features,
  price,
  isActive,
  isRecommended = false,
  loading = false,
  onSubscribe
}: NewPlanCardProps) => {
  if (loading) {
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: theme.palette.neutral[700],
          borderRadius: '8px',
          padding: '24px',
          flex: 1,
          minWidth: 280,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Skeleton
          variant="rounded"
          width="55%"
          height={24}
          sx={{ borderRadius: '4px', mb: '12px' }}
        />

        <Skeleton variant="text" width="100%" height={18} sx={{ mb: '4px' }} />
        <Skeleton variant="text" width="85%" height={18} sx={{ mb: '20px' }} />

        <Skeleton variant="text" width="95%" height={16} sx={{ mb: '16px' }} />

        <Stack gap="12px" sx={{ flex: 1 }}>
          {Array.from({ length: 7 }).map((_, i) => (
            <Stack key={i} direction="row" gap="8px" alignItems="center">
              <Skeleton
                variant="circular"
                width={8}
                height={8}
                sx={{ flexShrink: 0 }}
              />
              <Skeleton
                variant="text"
                width={`${70 + Math.random() * 25}%`}
                height={16}
              />
            </Stack>
          ))}
        </Stack>

        <Box mt="24px">
          <Stack direction="row" alignItems="baseline" gap="4px" mb="12px">
            <Skeleton
              variant="rounded"
              width={120}
              height={32}
              sx={{ borderRadius: '4px' }}
            />
            <Skeleton variant="text" width={30} height={16} />
          </Stack>
          <Skeleton
            variant="rounded"
            width="100%"
            height={44}
            sx={{ borderRadius: '8px' }}
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: isRecommended
          ? theme.palette.primary.main
          : isActive
          ? theme.palette.primary.main
          : theme.palette.neutral[700],
        borderRadius: '8px',
        padding: '24px',
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        ...(isRecommended && {
          borderWidth: '2px'
        })
      }}
    >
      {isRecommended && (
        <Box
          sx={{
            position: 'absolute',
            top: '-14px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            padding: '4px 16px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 700,
            whiteSpace: 'nowrap'
          }}
        >
          RECOMENDADO (mais popular)
        </Box>
      )}

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '20px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: '8px'
        }}
      >
        PLANO {title}
      </Typography>

      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 700,
          color: theme.palette.neutral[300],
          mb: '20px',
          lineHeight: 1.4
        }}
      >
        {subtitle}
      </Typography>

      <Typography
        sx={{
          fontSize: '14px',
          color: theme.palette.neutral[300],
          mb: '12px'
        }}
        dangerouslySetInnerHTML={{ __html: intro }}
      />

      <Stack gap="10px" sx={{ flex: 1 }}>
        {features.map((feature, index) => (
          <Stack key={index} direction="row" gap="8px" alignItems="flex-start">
            <Typography
              sx={{
                fontSize: '14px',
                color: theme.palette.neutral[300],
                lineHeight: 1.5,
                flexShrink: 0
              }}
            >
              →
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                color: theme.palette.neutral[200],
                lineHeight: 1.5
              }}
              dangerouslySetInnerHTML={{ __html: feature }}
            />
          </Stack>
        ))}
      </Stack>

      <Box mt="24px">
        <Stack direction="row" alignItems="baseline" gap="4px" mb="12px">
          <Typography sx={{ fontWeight: 700, fontSize: '28px' }}>
            R$ {price.toFixed(2).replace('.', ',')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: theme.palette.neutral[400]
            }}
          >
            /mês
          </Typography>
        </Stack>

        <Button
          fullWidth
          variantButton={isActive ? 'neutral' : 'primary'}
          disabled={isActive}
          onClick={onSubscribe}
        >
          {isActive ? 'Plano atual' : 'Contratar'}
        </Button>
      </Box>
    </Box>
  )
}

export default NewPlanCard
